import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import LanguageToggle from "@/components/LanguageToggle";

export default function Login() {
  const { login, formatApiErrorDetail } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t("common.success"));
      nav("/app");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] grain-bg flex flex-col" data-testid="login-page">
      <header className="container mx-auto px-4 md:px-8 py-6 flex items-center justify-between">
        <Logo />
        <LanguageToggle />
      </header>
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md bg-white rounded-xl border border-[#E5E2D9] shadow-sm p-8 fade-up">
          <h1 className="font-display font-bold text-3xl tracking-tight text-[#1A2F24] mb-1">
            {t("auth.login_title")}
          </h1>
          <p className="text-sm text-[#5C6B61] mb-7">{t("auth.login_subtitle")}</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Input
                id="email" type="email" required autoComplete="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 bg-[#F9F8F6] border-[#E5E2D9]"
              />
            </div>
            <div>
              <Label htmlFor="password">{t("auth.password")}</Label>
              <Input
                id="password" type="password" required autoComplete="current-password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 bg-[#F9F8F6] border-[#E5E2D9]"
              />
            </div>
            {err && (
              <div data-testid="login-error" className="text-sm text-[#C05A44] bg-[#FDF2EE] border border-[#F2D9D0] rounded-md p-3">
                {err}
              </div>
            )}
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-11 bg-[#1A2F24] hover:bg-[#2E3B32] text-white"
            >
              {loading ? t("common.loading") : t("auth.login_button")}
            </Button>
          </form>
          <p className="text-sm text-[#5C6B61] mt-6 text-center">
            {t("auth.no_account")}{" "}
            <Link to="/register" className="text-[#1A2F24] font-medium hover:underline" data-testid="link-to-register">
              {t("auth.to_register")}
            </Link>
          </p>
          <div className="flex justify-center gap-4 mt-4 text-xs text-[#5C6B61]">
            <Link to="/impressum" className="hover:text-[#1A2F24]" data-testid="login-impressum-link">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-[#1A2F24]" data-testid="login-datenschutz-link">Datenschutz</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
